const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/sagas/appointment.ts';
import { SagaIterator } from 'redux-saga';
import { put, select, takeEvery } from 'redux-saga/effects';
import { actionLoadStructuredAppointment, BOOK_APPOINTMENT, RESCHEDULE_APPOINTMENT } from 'App/actions/appointment';
import moment from 'moment';
import { KeyValuePair, KISIM_TIME_FORMAT } from '@cistec/common_web/defs/generalDefs';
import { NotLinkedPatient } from 'App/defs/types';
import { Appointment, AppointmentType } from '@cistec/common_web/defs/kisim';
import { goToAppointmentBookingConfirmation, goToAppointmentRescheduleConfirmation } from 'App/utils/history';
import { PatientClearingRequestData, requestPatientClearing } from 'App/actions/patientClearing';
import { selectAllowedAppointmentTypes } from 'App/store/allowedAppointmentTypes';

const getReferralsData = (
    appointment: Appointment,
    notLinkedPatient: NotLinkedPatient,
    formData: KeyValuePair
) => {
    const terminNr = appointment.TERMNR;
    const authorFromServiceRequest = `${notLinkedPatient.get('lastname')} ${notLinkedPatient.get('firstname')}`;
    const referralIDFromServiceRequest = `KISIMPatient_${terminNr}`;
    const specialityFromServiceRequest = appointment.KIND as string;

    const referralsData: PatientClearingRequestData = {
        Patient: {
            family: notLinkedPatient.get('lastname')?.toString(),
            given: notLinkedPatient.get('firstname')?.toString(),
            birthDate: moment(notLinkedPatient.get('dateofbirth')?.toString()).format(KISIM_TIME_FORMAT),
            email: notLinkedPatient.get('email')?.toString(),
            mobile: notLinkedPatient.get('mobile')?.toString(),
            steet: formData.streetAndNr,
            postalCode: formData.postalcode,
            city: formData.location,
            country: formData.country,
            sozversnr: formData.socialNr,
        },
        type: 'Terminbuchung',
        comments: formData.comment,
        reservedAppointment: terminNr,
        referralID: referralIDFromServiceRequest,
        speciality: specialityFromServiceRequest,
        author: authorFromServiceRequest,
        status: 'INSERT',
    };

    return referralsData;
};

// Navigates to booking confirmation page after successful booking
// Loads the new termin as structured appointment
// Also handles patient clearing (for now...)
function* handleBookAppointmentSuccess(result: KeyValuePair) {
    const { history, formData, notLinkedPatient } = result.meta;
    const appointment = result.payload as Appointment;

    if (notLinkedPatient) {
        const referralsData = getReferralsData(appointment, notLinkedPatient, formData);
        yield put(requestPatientClearing(referralsData));
    }

    const appointmentTypes: AppointmentType[] = yield select(selectAllowedAppointmentTypes);

    const terminNumber = appointment.TERMNR;
    yield put(actionLoadStructuredAppointment(terminNumber, appointmentTypes));
    goToAppointmentBookingConfirmation(history, terminNumber);
}

// Navigates to reschedule confirmation page after successful rescheduling
// Loads the updated termin as structured appointment
function* handleRescheduleAppointmentSuccess(result: KeyValuePair) {
    const { history, appointment } = result.meta;

    const appointmentTypes: AppointmentType[] = yield select(selectAllowedAppointmentTypes);

    yield put(actionLoadStructuredAppointment(appointment.TERMNR, appointmentTypes));
    goToAppointmentRescheduleConfirmation(history, appointment.TERMNR);
}

export function* appointmentsSaga(): SagaIterator {
    yield takeEvery(BOOK_APPOINTMENT.success, handleBookAppointmentSuccess);
    yield takeEvery(RESCHEDULE_APPOINTMENT.success, handleRescheduleAppointmentSuccess);
}
